import React from 'react';
import styled, { keyframes } from 'styled-components';

const rise = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 28px 32px;
  color: #fff;
  box-shadow: 0 18px 40px -12px rgba(79, 70, 229, 0.45);
  animation: ${rise} 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 640px) {
    padding: 22px 20px;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Bubble = styled.span`
  position: absolute;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.1);
  pointer-events: none;
  animation: ${float} ${(p) => p.$speed || 7}s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || 0}s;
`;

const IconBox = styled.div`
  display: flex;
  height: 52px;
  width: 52px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
`;

/**
 * Shared gradient header for the Sales pages.
 *  - gradient: Tailwind gradient classes (kept literal so Tailwind picks them up)
 *  - chips: small pills under the subtitle
 *  - actions: buttons / controls shown on the right
 */
export function PageHero({
    icon,
    title,
    subtitle,
    chips = [],
    actions = null,
    gradient = 'bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600',
}) {
    return (
        <Hero className={gradient}>
            <Bubble $speed={8} style={{ right: -40, top: -50, height: 180, width: 180 }} />
            <Bubble $speed={10} $delay={1.5} style={{ right: 140, bottom: -70, height: 140, width: 140 }} />
            <Bubble $speed={6} $delay={0.8} style={{ left: '38%', top: -30, height: 70, width: 70 }} />
            <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                    {icon ? <IconBox>{icon}</IconBox> : null}
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
                        {subtitle ? <p className="mt-1 max-w-2xl text-sm text-white/80">{subtitle}</p> : null}
                        {chips.length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {chips.filter(Boolean).map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
                {actions ? <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div> : null}
            </div>
        </Hero>
    );
}

// White pill button that sits nicely on the gradient hero.
export const HeroButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 14px;
  background: #fff;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 700;
  color: #4338ca;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.18);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(15, 23, 42, 0.24); }
  &:active { transform: scale(0.97); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const svg = { width: 26, height: 26, fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', viewBox: '0 0 24 24' };

export const HeroIcons = {
    profile: (
        <svg {...svg}><circle cx="12" cy="8" r="3.6" /><path d="M5 20a7 7 0 0114 0" /></svg>
    ),
    performance: (
        <svg {...svg}><path d="M4 19V9M10 19V5M16 19v-8M3 19h18" /></svg>
    ),
    offer: (
        <svg {...svg}><path d="M7 3h8l4 4v14H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></svg>
    ),
    work: (
        <svg {...svg}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2M3 13h18" /></svg>
    ),
    course: (
        <svg {...svg}><path d="M4 6.5L12 4l8 2.5-8 2.5-8-2.5z" /><path d="M4 11l8 2.5 8-2.5M4 15.5L12 18l8-2.5" /></svg>
    ),
    target: (
        <svg {...svg}><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.8" /></svg>
    ),
    plus: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
    ),
};
